import React from "react";
import { useLoginV2Context } from "../context/login_v2_context";
import {
  SignUpRoute,
  StartExperienceRoute,
} from "../../../../../core/routes/route_constants";
import { Heart, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import "../styles/animation.css";
import { TermsOfServiceRoute, PrivacyPolicyRoute } from "../../../../../core/routes/route_constants";
import { useNavigate } from "react-router-dom";

const LoginV2Comp = () => {
  const navigate = useNavigate();
  const {
    email,
    password,
    showPassword,
    rememberMe,
    isLoading,
    errorMessage,
    handleSubmit,
    togglePasswordVisibility,
    toggleRememberMe,
    handleEmailChange,
    handlePasswordChange,
    showOtpModal,
    handleOtpChange,
    otpCode,
    verifying,
    handleVerifyOtp,
    handleResendOtp,
    setOtpCode,
    resendTimer
  } = useLoginV2Context();

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpCode(digits);
    }
  };

  const handleResendOtpEvt = (e) => {
    handleResendOtp();
  };

  return (
    <>
      <div className="min-h-screen text-white flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-lg animate-fade-in mb-24">
          {/* Login Form Card with gradient border */}
          <div className="gradient-border p-0.5 shadow-xl shadow-red-950/10">
            <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-xl overflow-hidden relative">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>

              <div className="px-6 py-12">
                <div className="text-center mb-10">
                  <div className="mx-auto w-12 h-12 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-4">
                    <Heart
                      size={22}
                      className="text-white animate-pulse-soft"
                    />
                  </div>
                  <h1 className="text-3xl font-bold mb-1">Welcome Back</h1>
                  <p className="text-pink-300 mt-1 text-base">
                    Sign in to continue your experience
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-4 p-2 bg-red-900/30 border border-red-500/30 rounded-lg text-white text-sm">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                  <div className="space-y-1">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-pink-200"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-pink-400">
                        <Mail size={18} style={{ color: '#C19DA4' }} />
                      </div>
                      <input
                        // id="email"
                        // name="email"
                        type="email"
                        autoComplete="off"
                        required
                        value={email}
                        onChange={handleEmailChange}
                        className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-pink-200"
                      >
                        Password
                      </label>
                      <a
                        href="/forgot-password"
                        className="text-sm text-pink-400 hover:text-pink-300 transition-colors"
                      >
                        Forgot password?
                      </a>
                    </div>
                    <div className="relative">
                      <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock size={18} style={{ color: '#C19DA4' }} />
                      </div>
                      <input
                        // id="password"
                        // name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={handlePasswordChange}
                        className="text-base block w-full pl-10 pr-10 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                        placeholder="••••••••"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="text-pink-400 hover:text-pink-300 outline-none"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember_me"
                        name="remember_me"
                        type="checkbox"
                        className="h-4 w-4 bg-slate-950/80 border border-red-900/40 rounded text-red-700 focus:ring-red-700 focus:ring-offset-0 checkbox-glow"
                        checked={rememberMe}
                        onChange={toggleRememberMe}
                      />
                      <label
                        htmlFor="remember_me"
                        className="ml-2 block text-sm text-pink-300"
                      >
                        Remember me
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="text-base w-full flex justify-center items-center py-3.5 px-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg text-white font-medium transition-all duration-300 animate-float-subtle btn-hover-effect relative overflow-hidden group active:translate-y-0.5 active:shadow-none"
                  >
                    <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-500/0 via-white/10 to-red-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                    <span className="flex items-center">
                      {isLoading ? (
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : null}
                      <span>{isLoading ? 'Signing in...' : 'Sign in'}</span>
                      {!isLoading && <ArrowRight className="ml-2" size={16} />}
                    </span>
                  </button>
                </form>

                {/* New account registration link */}
                <div className="mt-8 text-center">
                  <p className="text-pink-300 text-xs">
                    Don't have an account?
                  </p>
                  <div className="flex justify-center">
                    <a
                      href="/sign-up"
                      className="text-sm py-2.5 mt-2 px-4 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg text-white font-medium transition-all duration-300 btn-hover-effect active:translate-y-0.5 active:shadow-none"
                    >
                      Start your experience
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="min-h-screen text-white flex items-center justify-center px-4 py-8 bg-black/60 fixed inset-0 z-50">
          <div className="w-full max-w-md animate-fade-in">
            <div className="gradient-border p-0.5 shadow-xl shadow-red-950/10">
              <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-lg overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>

                <div className="px-6 py-8 md:px-10 md:py-10">
                  <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-6">
                      <Lock
                        size={26}
                        className="text-white animate-pulse-soft"
                      />
                    </div>
                    <h1 className="text-3xl font-bold mb-1">
                      Verify Your Account
                    </h1>
                    <p className="text-pink-300 mt-2">
                      We've sent a code to your email
                    </p>
                  </div>

                  <div className="mb-6">
                    <p className="text-center text-pink-200 mb-4">
                      Enter the 6-digit code sent to {email || "your email"}
                    </p>

                    {errorMessage && (
                      <div className="mb-6 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-white text-sm">
                        {errorMessage}
                      </div>
                    )}

                    <div className="flex justify-between gap-2 mb-8">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="number"
                          inputMode="numeric"
                          maxLength="1"
                          value={otpCode[index]}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onPaste={handleOtpPaste}
                          className="text-base w-full aspect-square text-center text-lg font-bold bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white input-focus-effect outline-none"
                        />
                      ))}
                    </div>

                    <button
                      onClick={handleVerifyOtp}
                      disabled={verifying || otpCode.join("").length !== 6}
                      className="text-sm w-full flex justify-center items-center py-3.5 px-4 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg text-white font-medium transition-all duration-300 animate-float-subtle btn-hover-effect relative overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-500/0 via-white/10 to-red-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                      <span className="flex items-center">
                        {verifying ? (
                          <svg
                            className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : null}
                        <span>
                          {verifying ? "Verifying..." : "Verify & Continue"}
                        </span>
                        {!verifying && (
                          <ArrowRight className="ml-2" size={18} />
                        )}
                      </span>
                    </button>

                    <div className="mt-6 text-center">
                      <p className="text-pink-300 text-base">
                        Didn't receive a code?{" "}
                        <button
                          className={`text-red-400 font-medium hover:text-red-300 transition-colors ${resendTimer > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={handleResendOtpEvt}
                          disabled={resendTimer > 0}
                        >
                          Resend{resendTimer > 0 ? ` (${resendTimer}s)` : ''}
                        </button>
                      </p>
                    </div>

                    <div className="mt-8 flex items-center justify-center text-pink-400/80 text-sm">
                      <Lock size={14} className="mr-2" />
                      <p>Your connection is encrypted and secure</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginV2Comp;
